import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Tag,
  Save,
  ChevronLeft,
  ChevronRight,
  Trash2,
  AlertCircle,
  Lock,
  Plus,
  Building,
} from "lucide-react";
import api from "../../lib/api";
import { formatXOF } from "../../lib/i18n";

/**
 * Offres & Prix (iter-70).
 *
 * Centre de pilotage unique pour :
 *   1. les tarifs (prix adulte, enfant, frais traversée…)
 *   2. les disponibilités (fermetures de dates → grisées sur le tunnel public)
 *
 * Backend :
 *   - GET  /api/staff/config/offers         → liste enrichie (prix + blocked_dates)
 *   - PATCH /api/staff/config/offers/{id}   → sauvegarde tarifs OU dates (exclude_unset)
 *   - GET  /api/offers/{id}/blocked-dates   → lu par le BookingTunnel public
 */

// ═══════════════════════════════════════════════════════════════════════
// UNIVERSE MAPPING — identique à UniverseDashboard
// ═══════════════════════════════════════════════════════════════════════
const UNIVERSES = {
  beach_life: {
    label: "Beach Life",
    offer_ids: ["pass_day", "sunset", "brunch", "offres_loisirs", "special_event"],
  },
  hotel: {
    label: "Hôtel",
    offer_ids: ["hebergement", "le_kaai", "lounge"],
  },
};

// Some offers don't have a real availability calendar (custom flow) —
// e.g. special_event is date-scoped per event, salles/spa via catalog.
const AVAILABILITY_SUPPORTED = new Set([
  "pass_day", "sunset", "brunch", "offres_loisirs",
  "le_kaai", "lounge",
]);
// Hébergement is scheduled for Phase B (per-tier stock blocking).
const HEBERGEMENT_PHASE_B = new Set(["hebergement"]);

const PRICE_FIELDS = [
  { key: "price_adult", label: "Prix adulte (FCFA)" },
  { key: "price_child", label: "Prix enfant (FCFA)" },
  { key: "crossing_fee_xof", label: "Frais de traversée (FCFA)" },
];

export default function StaffOffersPricing() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeUniverse, setActiveUniverse] = useState("beach_life");
  const [activeOfferId, setActiveOfferId] = useState(null);
  const [activeTab, setActiveTab] = useState("pricing"); // pricing | availability

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/config/offers");
      setOffers(data.items || []);
      if (!activeOfferId && data.items?.length) {
        const first = data.items.find((o) => UNIVERSES.beach_life.offer_ids.includes(o.id));
        setActiveOfferId(first ? first.id : data.items[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les offres");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const universeOffers = useMemo(
    () => offers.filter((o) => UNIVERSES[activeUniverse].offer_ids.includes(o.id)),
    [offers, activeUniverse],
  );

  const activeOffer = useMemo(
    () => offers.find((o) => o.id === activeOfferId),
    [offers, activeOfferId],
  );

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-[1500px] mx-auto" data-testid="staff-offers-pricing">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A]">Offres &amp; Prix</h1>
        <p className="text-sm text-[#0A0A0A]/55 mt-1 max-w-2xl">
          Centre unique de gestion des tarifs et des disponibilités. Toute modification est
          appliquée en temps réel sur le Front Office.
        </p>
      </div>

      {/* Universe tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[#0A0A0A]/10 mb-6" data-testid="universe-tabs">
        {Object.entries(UNIVERSES).map(([id, cfg]) => (
          <button
            key={id}
            onClick={() => {
              setActiveUniverse(id);
              const first = offers.find((o) => cfg.offer_ids.includes(o.id));
              if (first) setActiveOfferId(first.id);
            }}
            className={`px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
              activeUniverse === id
                ? "border-[#B8922A] text-[#B8922A] font-medium"
                : "border-transparent text-[#0A0A0A]/55 hover:text-[#0A0A0A]"
            }`}
            data-testid={`universe-tab-${id}`}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/55 text-sm py-12 text-center">Chargement des offres…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar — offer list for current universe */}
          <aside className="space-y-1" data-testid="offer-sidebar">
            {universeOffers.map((o) => {
              const active = o.id === activeOfferId;
              const closedCount = (o.blocked_dates || []).length;
              return (
                <button
                  key={o.id}
                  onClick={() => setActiveOfferId(o.id)}
                  className={`w-full text-left px-4 py-3 border transition-colors ${
                    active
                      ? "border-[#B8922A] bg-[#FFF7DA]"
                      : "border-[#0A0A0A]/10 bg-white hover:border-[#B8922A]/40"
                  }`}
                  data-testid={`offer-select-${o.id}`}
                >
                  <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">
                    {o.id}
                  </div>
                  <div className="font-display-serif text-lg text-[#0A0A0A] mt-0.5">
                    {o.name_fr || o.title || o.id}
                  </div>
                  <div className="text-[0.72rem] text-[#0A0A0A]/60 mt-1">
                    {o.price_adult > 0 ? formatXOF(o.price_adult) : "Sur demande"}
                    {closedCount > 0 && (
                      <span className="text-[#B8922A] ml-2">· {closedCount} date{closedCount > 1 ? "s" : ""} fermée{closedCount > 1 ? "s" : ""}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Content — pricing & availability for selected offer */}
          <section className="bg-white border border-[#0A0A0A]/8 p-6" data-testid="offer-detail">
            {!activeOffer ? (
              <div className="text-[#0A0A0A]/55 text-sm">Sélectionnez une offre à gauche.</div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A]">
                    {UNIVERSES[activeUniverse].label}
                  </div>
                  <h2 className="font-display-serif text-2xl md:text-3xl text-[#0A0A0A] mt-1">
                    {activeOffer.name_fr || activeOffer.id}
                  </h2>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-[#0A0A0A]/10 mb-6" data-testid="offer-subtabs">
                  <TabButton
                    active={activeTab === "pricing"}
                    onClick={() => setActiveTab("pricing")}
                    icon={<Tag size={12} />}
                    testid="subtab-pricing"
                  >
                    Tarifs
                  </TabButton>
                  <TabButton
                    active={activeTab === "availability"}
                    onClick={() => setActiveTab("availability")}
                    icon={<CalendarIcon size={12} />}
                    testid="subtab-availability"
                  >
                    Disponibilités
                  </TabButton>
                </div>

                {activeTab === "pricing" && (
                  <PricingPanel offer={activeOffer} onSaved={refresh} />
                )}
                {activeTab === "availability" && (
                  <AvailabilityPanel offer={activeOffer} onSaved={refresh} />
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
function TabButton({ active, onClick, icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border-b-2 -mb-px inline-flex items-center gap-1.5 ${
        active
          ? "border-[#B8922A] text-[#B8922A] font-medium"
          : "border-transparent text-[#0A0A0A]/55 hover:text-[#0A0A0A]"
      }`}
      data-testid={testid}
    >
      {icon}
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PRICING PANEL
// ═══════════════════════════════════════════════════════════════════════
function PricingPanel({ offer, onSaved }) {
  const isHebergement = offer.id === "hebergement";
  const [vals, setVals] = useState({
    price_adult: offer.price_adult ?? 0,
    price_child: offer.price_child ?? 0,
    crossing_fee_xof: offer.crossing_fee_xof ?? 0,
  });
  // Per-tier prices for hebergement — mirrors offer.room_tiers structure.
  const [tiers, setTiers] = useState(offer.room_tiers || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVals({
      price_adult: offer.price_adult ?? 0,
      price_child: offer.price_child ?? 0,
      crossing_fee_xof: offer.crossing_fee_xof ?? 0,
    });
    setTiers(offer.room_tiers || []);
  }, [offer.id, offer.price_adult, offer.price_child, offer.crossing_fee_xof, offer.room_tiers]);

  const updateTierPrice = (tierId, price) => {
    setTiers((prev) => prev.map((t) => (t.id === tierId ? { ...t, price: price } : t)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (isHebergement) {
        // Send only the room_tiers array — no adult/child on this offer.
        payload.room_tiers = tiers.map((t) => ({
          id: t.id,
          name_fr: t.name_fr,
          name_en: t.name_en,
          price: Number(t.price) || 0,
          inventory: t.inventory,
        }));
        if (offer.crossing_fee_xof != null) {
          payload.crossing_fee_xof = Number(vals.crossing_fee_xof) || 0;
        }
      } else {
        payload.price_adult = Number(vals.price_adult) || 0;
        payload.price_child = Number(vals.price_child) || 0;
        if (offer.crossing_fee_xof != null) {
          payload.crossing_fee_xof = Number(vals.crossing_fee_xof) || 0;
        }
      }
      await api.patch(`/staff/config/offers/${offer.id}`, payload);
      toast.success("Tarifs sauvegardés — synchronisés sur le site");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const hasCrossing = offer.crossing_fee_xof != null;

  return (
    <div data-testid="pricing-panel">
      {isHebergement ? (
        <div className="mb-6" data-testid="hebergement-tier-pricing">
          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-3">
            Tarif par nuit et par catégorie de chambre
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tiers.map((t) => (
              <PriceInput
                key={t.id}
                label={`${t.name_fr || t.id} · Prix / nuit (FCFA)`}
                value={t.price}
                onChange={(v) => updateTierPrice(t.id, v)}
                testid={`price-tier-${t.id}`}
              />
            ))}
          </div>
          {hasCrossing && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <PriceInput
                label="Frais de traversée (FCFA)"
                value={vals.crossing_fee_xof}
                onChange={(v) => setVals({ ...vals, crossing_fee_xof: v })}
                testid="price-crossing_fee_xof"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {PRICE_FIELDS.filter((f) => f.key !== "crossing_fee_xof" || hasCrossing).map((f) => (
            <PriceInput
              key={f.key}
              label={f.label}
              value={vals[f.key]}
              onChange={(v) => setVals({ ...vals, [f.key]: v })}
              testid={`price-${f.key}`}
            />
          ))}
        </div>
      )}

      <div className="text-[0.72rem] text-[#0A0A0A]/55 mb-4 leading-relaxed">
        <AlertCircle size={11} className="inline -mt-0.5 mr-1 text-[#B8922A]" />
        Toute modification est immédiatement appliquée sur le tunnel de réservation et sur les pages
        publiques de l&apos;offre.
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="save-pricing-btn"
      >
        <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer les tarifs"}
      </button>
    </div>
  );
}

function PriceInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          min="0"
          step="500"
          value={value || ""}
          onChange={(e) => onChange(parseInt(e.target.value || 0))}
          className="w-full border border-[#0A0A0A]/15 px-3 py-2.5 text-sm focus:border-[#B8922A] outline-none tabular-nums"
          data-testid={testid}
        />
      </div>
      {Number(value) > 0 && (
        <div className="text-[0.68rem] text-[#0A0A0A]/50 mt-1 tabular-nums">
          = {formatXOF(value)}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// AVAILABILITY PANEL — Beach Life closure calendar
// ═══════════════════════════════════════════════════════════════════════
function AvailabilityPanel({ offer, onSaved }) {
  const supported = AVAILABILITY_SUPPORTED.has(offer.id);
  const isHebergement = HEBERGEMENT_PHASE_B.has(offer.id);

  const [blocked, setBlocked] = useState(new Set(offer.blocked_dates || []));
  const [reason, setReason] = useState(offer.blocked_reason || "");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(new Set()); // dates awaiting save

  useEffect(() => {
    setBlocked(new Set(offer.blocked_dates || []));
    setReason(offer.blocked_reason || "");
    setPending(new Set());
  }, [offer.id, offer.blocked_dates, offer.blocked_reason]);

  if (isHebergement) {
    return <HebergementAvailability offer={offer} onSaved={onSaved} />;
  }
  if (!supported) {
    return (
      <div className="bg-[#FAFAF7] border border-[#0A0A0A]/8 p-8 text-center">
        <p className="text-sm text-[#0A0A0A]/60">
          Cette offre utilise un calendrier dédié (catalogue événement, salle, spa) — géré depuis
          sa propre section.
        </p>
      </div>
    );
  }

  const toggle = (iso) => {
    const next = new Set(blocked);
    if (next.has(iso)) next.delete(iso);
    else next.add(iso);
    setBlocked(next);
    setPending((p) => new Set([...p, iso]));
  };

  const clearAll = () => {
    if (!window.confirm("Rouvrir toutes les dates fermées ?")) return;
    setBlocked(new Set());
    setPending(new Set(["__all__"]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/staff/config/offers/${offer.id}`, {
        blocked_dates: Array.from(blocked).sort(),
        blocked_reason: reason,
      });
      toast.success(
        blocked.size === 0
          ? "Toutes les dates rouvertes à la réservation"
          : `${blocked.size} date${blocked.size > 1 ? "s" : ""} fermée${blocked.size > 1 ? "s" : ""}`,
      );
      setPending(new Set());
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const changeMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const nm = new Date(y, m - 1 + delta, 1);
    setMonth(`${nm.getFullYear()}-${String(nm.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div data-testid="availability-panel">
      <div className="bg-[#FAFAF7] border-l-2 border-[#B8922A] p-4 mb-5 text-[0.78rem] text-[#0A0A0A]/70 leading-relaxed">
        <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-1 flex items-center gap-1">
          <Lock size={11} /> Comment ça marche ?
        </div>
        Cliquez sur une date pour la basculer entre <b>ouverte</b> et <b>fermée</b>. Les dates
        fermées sont automatiquement grisées sur le tunnel public — aucun visiteur ne peut y
        réserver. N&apos;oubliez pas d&apos;enregistrer.
      </div>

      <Calendar
        month={month}
        onChangeMonth={setMonth}
        blocked={blocked}
        onToggle={toggle}
        onPrev={() => changeMonth(-1)}
        onNext={() => changeMonth(1)}
        pending={pending}
      />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="md:col-span-2">
          <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1.5">
            Motif interne (facultatif)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Privatisation · Maintenance · Événement spécial…"
            className="w-full border border-[#0A0A0A]/15 px-3 py-2.5 text-sm focus:border-[#B8922A] outline-none"
            data-testid="blocked-reason"
          />
        </div>
        <div className="text-[0.72rem] text-[#0A0A0A]/60 flex items-center gap-2 tabular-nums">
          <span className="inline-block w-3 h-3 bg-[#B8922A]" />
          {blocked.size} date{blocked.size > 1 ? "s" : ""} fermée{blocked.size > 1 ? "s" : ""}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving || pending.size === 0}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="save-availability-btn"
        >
          <Save size={13} /> {saving ? "Enregistrement…" : pending.size === 0 ? "Aucun changement" : "Enregistrer"}
        </button>
        {blocked.size > 0 && (
          <button
            onClick={clearAll}
            className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 hover:text-red-600 inline-flex items-center gap-1"
            data-testid="clear-availability-btn"
          >
            <Trash2 size={12} /> Tout rouvrir
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
function Calendar({ month, blocked, onToggle, onPrev, onNext, pending }) {
  const [year, m] = month.split("-").map(Number);
  const firstDay = new Date(year, m - 1, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const label = firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white border border-[#0A0A0A]/10 p-5" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onPrev} className="p-2 hover:bg-[#FAFAF7]" data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <div className="font-display-serif text-xl text-[#0A0A0A] capitalize">{label}</div>
        <button onClick={onNext} className="p-2 hover:bg-[#FAFAF7]" data-testid="cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
          <div key={d} className="text-[0.55rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 text-center pb-1">
            {d}
          </div>
        ))}
        {cells.map((d, idx) => {
          if (d === null) return <div key={`e-${idx}`} />;
          const iso = `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const isBlocked = blocked.has(iso);
          const isToday = iso === today;
          const isPast = iso < today;
          const isPending = pending.has(iso);
          return (
            <button
              key={iso}
              onClick={() => onToggle(iso)}
              disabled={isPast}
              className={`aspect-square flex flex-col items-center justify-center text-sm transition-colors relative
                ${isBlocked
                  ? "bg-[#B8922A] text-white hover:bg-[#9c7c1f]"
                  : isPast
                    ? "bg-[#0A0A0A]/5 text-[#0A0A0A]/25 cursor-not-allowed"
                    : "bg-white border border-[#0A0A0A]/10 hover:border-[#B8922A] hover:bg-[#FFF7DA]"}
                ${isToday && !isBlocked ? "ring-1 ring-[#B8922A]" : ""}
              `}
              data-testid={`avail-day-${iso}`}
            >
              <span className="tabular-nums font-medium">{d}</span>
              {isBlocked && (
                <span className="text-[0.5rem] uppercase tracking-[0.18em] mt-0.5">Fermé</span>
              )}
              {isPending && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[0.65rem] text-[#0A0A0A]/60">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-white border border-[#0A0A0A]/20" /> Ouvert</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#B8922A]" /> Fermé</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#0A0A0A]/10" /> Passé</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2 h-2 bg-red-500 rounded-full" /> Modification non enregistrée</span>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// HEBERGEMENT AVAILABILITY (iter-71 · Phase B)
// ═══════════════════════════════════════════════════════════════════════
const BLOCK_CATEGORIES = [
  { id: "corporate", label: "Corporate" },
  { id: "group", label: "Groupe" },
  { id: "privatization", label: "Privatisation" },
  { id: "maintenance", label: "Maintenance" },
  { id: "internal", label: "Réservation interne" },
  { id: "other", label: "Autre" },
];

function colorForPct(pct) {
  if (pct >= 90) return "bg-red-500/85 text-white";
  if (pct >= 60) return "bg-orange-400/85 text-white";
  if (pct >= 30) return "bg-yellow-300/70 text-[#0A0A0A]";
  return "bg-green-500/15 text-green-800";
}

function HebergementAvailability() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const range = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2, "0")}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return { from: first, to: last };
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const [avail, blk] = await Promise.all([
        api.get(`/staff/hebergement/availability?from_date=${range.from}&to_date=${range.to}`),
        api.get(`/staff/hebergement-blocks?from_date=${range.from}&to_date=${range.to}`),
      ]);
      setData(avail.data);
      setBlocks(blk.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [range.from, range.to]);

  const removeBlock = async (id) => {
    if (!window.confirm("Supprimer ce blocage ? Les chambres redeviennent disponibles.")) return;
    try {
      await api.delete(`/staff/hebergement-blocks/${id}`);
      toast.success("Bloc supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const changeMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const nm = new Date(y, m - 1 + delta, 1);
    setMonth(`${nm.getFullYear()}-${String(nm.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div data-testid="hebergement-availability">
      <div className="bg-[#FAFAF7] border-l-2 border-[#B8922A] p-4 mb-5 text-[0.78rem] text-[#0A0A0A]/70 leading-relaxed">
        <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-1 flex items-center gap-1">
          <Lock size={11} /> Gestion du stock
        </div>
        Chaque cellule affiche le nombre de chambres <b>disponibles</b> pour ce tier ce jour-là.
        Le calcul <code>stock − blocs − réservations</code> se fait en temps réel : ajouter un bloc
        pour un groupe / une maintenance / une privatisation réduit immédiatement l&apos;inventaire
        visible côté public.
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-[#FAFAF7]" data-testid="heb-cal-prev">
          <ChevronLeft size={16} />
        </button>
        <div className="font-display-serif text-xl text-[#0A0A0A] capitalize">
          {new Date(range.from).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
        </div>
        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-[#FAFAF7]" data-testid="heb-cal-next">
          <ChevronRight size={16} />
        </button>
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/55 text-sm py-12 text-center">Chargement…</div>
      ) : !data?.tiers?.length ? (
        <div className="text-[#0A0A0A]/55 text-sm py-12 text-center">Aucun tier configuré.</div>
      ) : (
        <div className="overflow-x-auto" data-testid="heb-availability-grid">
          <table className="w-full min-w-[900px] border-collapse text-[0.72rem]">
            <thead>
              <tr className="border-b border-[#0A0A0A]/10">
                <th className="text-left py-2 pr-3 text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 sticky left-0 bg-white">Tier</th>
                {data.tiers[0].dates.map((d) => {
                  const day = new Date(d.date).getDate();
                  const wd = new Date(d.date).toLocaleDateString("fr-FR", { weekday: "narrow" });
                  return (
                    <th key={d.date} className="text-center py-2 px-1 text-[0.6rem] tabular-nums text-[#0A0A0A]/60 min-w-[36px]">
                      <div className="text-[0.55rem] uppercase">{wd}</div>
                      <div className="font-medium text-[#0A0A0A] text-[0.72rem]">{day}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.tiers.map((t) => (
                <tr key={t.id} className="border-b border-[#0A0A0A]/5">
                  <td className="py-1.5 pr-3 sticky left-0 bg-white">
                    <div className="text-[0.78rem] text-[#0A0A0A] font-medium">{t.name}</div>
                    <div className="text-[0.6rem] text-[#0A0A0A]/55 uppercase tracking-[0.18em]">
                      Stock : {t.inventory}
                    </div>
                  </td>
                  {t.dates.map((cell) => (
                    <td
                      key={cell.date}
                      className={`text-center py-1 px-0.5 tabular-nums ${colorForPct(cell.utilization_pct)}`}
                      title={`${cell.date}\nStock ${cell.inventory} − Bloc ${cell.blocked} − Rés ${cell.reserved} = ${cell.available} dispo (${cell.utilization_pct}%)`}
                      data-testid={`heb-cell-${t.id}-${cell.date}`}
                    >
                      <div className="text-[0.78rem] font-medium">{cell.available}</div>
                      {(cell.blocked > 0 || cell.reserved > 0) && (
                        <div className="text-[0.55rem] opacity-80">
                          {cell.blocked > 0 ? `−${cell.blocked}` : ""}
                          {cell.blocked > 0 && cell.reserved > 0 ? " / " : ""}
                          {cell.reserved > 0 ? `−${cell.reserved}` : ""}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[0.65rem] text-[#0A0A0A]/60">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 bg-green-500/15" /> ≤ 30 % occupé</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 bg-yellow-300/70" /> 30 – 60 %</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 bg-orange-400/85" /> 60 – 90 %</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 bg-red-500/85" /> ≥ 90 % (complet)</span>
      </div>

      {/* Existing blocks */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display-serif text-lg text-[#0A0A0A]">Blocs actifs sur ce mois</h4>
          <button
            onClick={() => setShowForm(true)}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="add-block-btn"
          >
            <Plus size={13} /> Nouveau blocage
          </button>
        </div>
        {blocks.length === 0 ? (
          <div className="bg-[#FAFAF7] border border-dashed border-[#0A0A0A]/15 p-6 text-center text-sm text-[#0A0A0A]/55">
            Aucun blocage actif sur ce mois.
          </div>
        ) : (
          <ul className="divide-y divide-[#0A0A0A]/8 border border-[#0A0A0A]/10" data-testid="blocks-list">
            {blocks.map((b) => (
              <li key={b.id} className="flex items-center gap-3 px-4 py-3">
                <Building size={14} className="text-[#B8922A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A0A0A]">
                    <b>{b.quantity}</b> × {b.tier_name || b.tier_id}{" "}
                    <span className="text-[0.72rem] text-[#0A0A0A]/60">
                      · du {b.start_date} au {b.end_date}
                    </span>
                  </div>
                  <div className="text-[0.7rem] text-[#0A0A0A]/60 mt-0.5">
                    {b.category ? BLOCK_CATEGORIES.find((c) => c.id === b.category)?.label || b.category : ""}
                    {b.reason ? ` — ${b.reason}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => removeBlock(b.id)}
                  className="text-[#0A0A0A]/40 hover:text-red-600 p-1"
                  data-testid={`delete-block-${b.id.slice(0, 8)}`}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && (
        <BlockForm
          tiers={data?.tiers || []}
          defaultFrom={range.from}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function BlockForm({ tiers, defaultFrom, onClose, onCreated }) {
  const [form, setForm] = useState({
    tier_id: tiers[0]?.id || "",
    start_date: defaultFrom,
    end_date: defaultFrom,
    quantity: 1,
    category: "corporate",
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const tierInfo = tiers.find((t) => t.id === form.tier_id);

  const submit = async () => {
    if (!form.tier_id) { toast.error("Choisissez un tier"); return; }
    if (form.end_date < form.start_date) { toast.error("La date de fin doit être ≥ date de début"); return; }
    if (!form.quantity || form.quantity < 1) { toast.error("Quantité invalide"); return; }
    if (tierInfo && form.quantity > tierInfo.inventory) {
      toast.error(`Le tier ${tierInfo.name} a un stock max de ${tierInfo.inventory}`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/staff/hebergement-blocks", form);
      toast.success("Bloc créé");
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white max-w-lg w-full p-6" data-testid="block-form-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display-serif text-xl text-[#0A0A0A]">Bloquer des chambres</h3>
          <button onClick={onClose} className="text-[#0A0A0A]/55 hover:text-[#0A0A0A]" data-testid="block-form-close">✕</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">Tier *</label>
            <select
              value={form.tier_id}
              onChange={(e) => setForm({ ...form, tier_id: e.target.value })}
              className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
              data-testid="block-tier"
            >
              {tiers.map((t) => (
                <option key={t.id} value={t.id}>{t.name} · stock {t.inventory}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">Date de début *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                data-testid="block-start"
              />
            </div>
            <div>
              <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">Date de fin *</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                data-testid="block-end"
              />
            </div>
          </div>
          <div>
            <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">
              Nombre de chambres * {tierInfo && <span className="text-[#0A0A0A]/45 normal-case tracking-normal">(max {tierInfo.inventory})</span>}
            </label>
            <input
              type="number"
              min="1"
              max={tierInfo?.inventory || 999}
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || 0) })}
              className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none tabular-nums"
              data-testid="block-quantity"
            />
          </div>
          <div>
            <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">Catégorie</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
              data-testid="block-category"
            >
              {BLOCK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 block mb-1">Motif (facultatif)</label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="ex. Groupe Acme (contrat #4521)"
              className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
              data-testid="block-reason"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0A0A0A]/70 hover:text-[#0A0A0A]">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-gold" data-testid="block-submit">
            {saving ? "Création…" : "Bloquer"}
          </button>
        </div>
      </div>
    </div>
  );
}
